// The one price book, client side (docs/product/PLAN_CAPABILITY_PRICING_SPEC.md section 4).
//
// A school pays `max(rate x active students, minimum)` for a year; a term is that annual figure
// pro-rated by its months and multiplied by its own `price_bps`. Every number here comes from the
// SERVED `pricing_model` payload of GET /api/public/marketing — there are no fallback constants,
// because a fallback price is a price nobody approved being published under our name.
//
// ALL money is INTEGER PAISE. Render only through inr()/inrFull() from lib/utils at the edge.
// The backend mirror is `core/billing.annual_base_paise` / `term_price_paise`; the two round the
// same way (half-up, in integer arithmetic) and are asserted against the same worked figures.

export const TIER_ORDER = ["plan_free", "plan_starter", "plan_growth", "plan_premium"];

const MONTHLY_BASELINE = Object.freeze({
  key: "monthly", label: "Monthly", months: 1, price_bps: 10000, discount_bps: 0,
});

const int = (v, fallback = 0) => {
  const n = Math.round(Number(v));
  return Number.isFinite(n) ? n : fallback;
};

/**
 * The served `pricing_model` -> the page's model.
 *
 * Returns `{ tiers, terms, enterprise, monthlyUpliftBps, verified }`. `verified` is false when the
 * payload carries no usable tiers; every surface checks it before rendering a figure, so a failed
 * or partial fetch shows "we couldn't load prices" rather than a number the school might believe.
 */
export function fromConfig(pricingModel) {
  const pm = pricingModel && typeof pricingModel === "object" ? pricingModel : {};
  const rawTiers = Array.isArray(pm.tiers) ? pm.tiers : [];
  const tiers = rawTiers
    .filter((t) => t && TIER_ORDER.includes(t.plan_id))
    .map((t) => ({
      key: String(t.key || ""),
      planId: String(t.plan_id),
      ratePaise: int(t.rate_paise_per_student_year, -1),
      floorPaise: int(t.floor_paise_per_year, -1),
    }))
    .filter((t) => t.ratePaise >= 0 && t.floorPaise >= 0)
    // The ladder's order is the product's, not the payload's: a reordered array must not reorder
    // the page and quietly present Pro as the cheapest option.
    .sort((a, b) => TIER_ORDER.indexOf(a.planId) - TIER_ORDER.indexOf(b.planId));

  const terms = (Array.isArray(pm.subscription_terms) ? pm.subscription_terms : [])
    .filter((t) => t && typeof t.key === "string" && int(t.months) >= 1 && int(t.price_bps) >= 1)
    .map((t) => ({
      key: String(t.key),
      label: String(t.label || t.key),
      months: int(t.months, 1),
      price_bps: int(t.price_bps, 10000),
      // Negative for the monthly term: it is a surcharge. Callers branch on the sign rather than
      // labelling every term a saving.
      discount_bps: 10000 - int(t.price_bps, 10000),
    }));

  const ent = pm.enterprise && typeof pm.enterprise === "object" ? pm.enterprise : {};
  return {
    tiers,
    terms: terms.length ? terms : [MONTHLY_BASELINE],
    monthlyUpliftBps: int(pm.monthly_uplift_bps),
    // Which plans actually SELL the monthly term. Served, never guessed: the rule that really
    // refuses the sale is `billing.assert_term_available`, and a client-side copy of it would
    // drift the day the policy moves. Empty means "offer no monthly option", which is the safe
    // reading of a payload we could not parse.
    monthlyEligiblePlans: Array.isArray(pm.monthly_eligible_plans)
      ? pm.monthly_eligible_plans.map(String)
      : [],
    enterprise: {
      ratePaise: int(ent.rate_paise_per_student_year),
      platformFeePaise: int(ent.platform_fee_paise_per_year),
      minimumPaise: int(ent.minimum_paise_per_year),
      selfServeMaxStudents: int(ent.self_serve_max_students),
      steps: (Array.isArray(ent.volume_steps) ? ent.volume_steps : [])
        .map((s) => ({ minStudents: int(s.min_students), discountBps: int(s.discount_bps) }))
        .filter((s) => s.minStudents > 0)
        .sort((a, b) => a.minStudents - b.minStudents),
    },
    verified: tiers.length === TIER_ORDER.length,
  };
}

/** A safe non-negative integer student count (garbage, negatives and blanks all read as 0). */
export function toStudents(value) {
  const n = Math.floor(Number(value));
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/** `max(rate x students, minimum)` in paise. Zero students bills zero. */
export function annualPaise(tier, students) {
  if (!tier) return 0;
  const n = toStudents(students);
  if (n <= 0) return 0;
  return Math.max(n * tier.ratePaise, tier.floorPaise);
}

/** What one term costs from the annual base: annual x months/12 x price_bps/10000, half-up. */
export function termPricePaise(annual, term) {
  const a = Math.max(0, int(annual));
  const months = Math.max(1, int(term?.months, 1));
  const bps = Math.max(0, int(term?.price_bps, 10000));
  return Math.floor((a * months * bps + 60000) / 120000);
}

/** The annual price stated per month — a comparison aid, never a charge. */
export function monthlyEquivalentPaise(annual) {
  return Math.floor((Math.max(0, int(annual)) + 6) / 12);
}

/**
 * The indicative Enterprise figure for a roster past the self-serve ceiling.
 *
 * Labelled indicative everywhere it renders: the real number is a quote, because Enterprise also
 * carries implementation, SLA and scope that no calculator can see.
 */
export function enterpriseIndicative(students, model) {
  const ent = model?.enterprise || {};
  const n = toStudents(students);
  const step = (ent.steps || []).reduce(
    (best, s) => (n >= s.minStudents ? Math.max(best, s.discountBps) : best), 0,
  );
  const perStudent = Math.floor((n * int(ent.ratePaise) * (10000 - step) + 5000) / 10000);
  const subtotal = perStudent + int(ent.platformFeePaise);
  return {
    students: n,
    discountBps: step,
    perStudentPaise: perStudent,
    platformFeePaise: int(ent.platformFeePaise),
    minimumPaise: int(ent.minimumPaise),
    minimumApplied: subtotal < int(ent.minimumPaise),
    annualPaise: Math.max(subtotal, int(ent.minimumPaise)),
  };
}
